<template>
  <div
    class="task-card-detail d-flex flex-column gap-2"
    :class="{
      'task-card-detail--selected': selected,
      'task-card-detail--peek': peekMode,
    }"
    data-testid="task-card-detail"
    @click="onCardClick"
  >
    <div class="task-card-detail__meta">
      <div class="task-card-detail__meta-left">
        <v-checkbox
          v-if="showCheckbox && canSelectForDueDateUpdate"
          dense
          hide-details
          class="task-card-detail__checkbox ma-0 pa-0"
          :input-value="selected"
          color="primary"
          data-testid="task-card-detail__checkbox"
          @change="$emit('selection-change', $event)"
          @click.stop
        />

        <span
          v-if="task._id && navigationMode === 'route'"
          class="task-card-detail__ref task-card-detail__link"
          role="link"
          tabindex="0"
          data-testid="task-card-detail__ref"
          @click.stop="navigateToDetail"
          @keydown.enter.stop="navigateToDetail"
        >
          {{ task.ref }}
        </span>

        <span
          v-else
          class="task-card-detail__ref"
          data-testid="task-card-detail__ref"
        >
          {{ task.ref }}
        </span>

        <span
          v-if="task.locationName"
          class="task-card-detail__location"
          data-testid="task-card-detail__location"
        >
          <v-icon x-small class="task-card-detail__location-icon">mdi-map-marker-outline</v-icon>
          {{ task.locationName }}
        </span>
      </div>

      <span
        v-if="task.priority && peekMode"
        class="task-card-detail__priority-wrap"
        data-testid="task-card-detail__priority"
      >
        <TaskPriorityDisplay :priority="task.priority" variant="cell" />
      </span>

      <span
        v-else-if="task.priority"
        class="task-card-detail__priority"
        :class="`task-card-detail__priority--${priorityKey}`"
        data-testid="task-card-detail__priority"
      >
        {{ task.priority }}
      </span>
    </div>

    <span
      v-if="task._id && navigationMode === 'route'"
      class="task-card-detail__name two-line-clamp task-card-detail__link"
      role="link"
      tabindex="0"
      data-testid="task-card-detail__name"
      @click.stop="navigateToDetail"
      @keydown.enter.stop="navigateToDetail"
    >
      {{ task.description }}
    </span>

    <span
      v-else
      class="task-card-detail__name two-line-clamp"
      data-testid="task-card-detail__name"
    >
      {{ task.description }}
    </span>

    <div class="task-card-detail__footer">
      <div
        v-if="task.isOverdue"
        class="task-card-detail__overdue"
        data-testid="task-card-detail__overdue"
      >
        <span class="task-card-detail__overdue-icon">
          <img
            src="/img/icons/fbc-icons/DueDateIcon.svg"
            alt="Clock"
            class="task-card-detail__overdue-icon-image"
          />
        </span>
        <span>{{ task.overdueDays }}</span>
      </div>

      <div v-else class="task-card-detail__footer-spacer" />

      <StatusIndicator
        :variant="statusVariant"
        :custom-label="task.statusBadge"
        size="sm"
        data-testid="task-card-detail__status"
        outlined
      />
    </div>
  </div>
</template>

<script>
import './TaskCardDetail.scss';

import StatusIndicator from '@/components/StatusIndicator.vue';
import TaskPriorityDisplay from '@/modules/financialclose/views/Project/TaskList/components/TaskPriorityDisplay/TaskPriorityDisplay.vue';

import {
  TASK_PRIORITY_CSS_MODIFIER,
  TASK_PRIORITY_CSS_MODIFIER_DEFAULT,
  TASK_STAGES,
} from '../../utils/constants';
import { getTaskStageStatusVariant } from '../../utils/tasklistHelpers';

import { HORIZON_VIEWS } from '../../utils/horizonConstants';
import { openTaskDetailPage } from '../../utils/tasklistUtils';

export default {
  name: 'TaskCardDetail',

  components: {
    StatusIndicator,
    TaskPriorityDisplay,
  },

  props: {
    task: {
      type: Object,
      required: true,
    },
    selected: {
      type: Boolean,
      default: false,
    },
    /** route = navigate on click; emit = emit select-task (Quick Peek) */
    navigationMode: {
      type: String,
      default: 'route',
      validator: (v) => ['route', 'emit'].includes(v),
    },
    showCheckbox: {
      type: Boolean,
      default: false,
    },
    peekMode: {
      type: Boolean,
      default: false,
    },
    checklistId: {
      type: String,
      default: '',
    },
    tasklistView: {
      type: String,
      default: null,
    },
  },

  computed: {
    priorityKey() {
      return (
        TASK_PRIORITY_CSS_MODIFIER[this.task.priority] ||
        TASK_PRIORITY_CSS_MODIFIER_DEFAULT
      );
    },

    statusVariant() {
      return getTaskStageStatusVariant(this.task);
    },

    canSelectForDueDateUpdate() {
      return this.task.stage !== TASK_STAGES.CLOSED;
    },
  },

  methods: {
    onCardClick() {
      if (this.navigationMode === 'emit') {
        this.$emit('select-task', this.task);
      }
    },

    navigateToDetail() {
      openTaskDetailPage({
        router: this.$router,
        store: this.$store,
        task: this.task,
        checklistId: this.checklistId || this.$route.params.projectId || this.$route.params.id,
        tasklistView: this.tasklistView || HORIZON_VIEWS.HORIZON,
      });
    },
  },
};
</script>
